import React, { useEffect, useState } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import {
  getGameSystems,
  handlePaging,
  handlePagingAll,
} from "../../components/api/APIUtilities";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { mapData } from "../events/utils/event";
import moment from "moment";
import { postNotification } from "../../components/utilities/Notifications";
import OrgDashEvents from "./OrganizeDashboard/OrgDashEvents";
import OrgDashTicketSales from "./OrganizeDashboard/OrgDashTicketSales";
import OrgDashOrganizations from "./OrganizeDashboard/OrgDashOrganizations";
import OrgDashCircuitTokens from "./OrganizeDashboard/OrgDashCircuitTokens";
import { fetchCircuitTokenData } from "../../components/utilities/CircuitTokenUtils";
import { getData } from "../../components/utilities/UtilityFunctions";

const searchLimit = 30;

export default function OrganizeDashboard(props) {
  const { authContext } = props;

  const [gameSystems, setGameSystems] = useState(null);

  const [events, setEvents] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [eventNextKey, setEventNextKey] = useState(null);
  const [tokenEventsData, setTokenEventsData] = useState(null);



  const [organizations, setOrganizations] = useState(null);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);

  const [circuitTokens, setCircuitTokens] = useState(null);
  const [circuitTokensLoading, setCircuitTokensLoading] = useState(true);



  const getEvents = async () => {
    let _gameSystems = gameSystems;

    if (!_gameSystems) {
      _gameSystems = await getGameSystems({
        inclSystem: true,
      });
      setGameSystems(_gameSystems);
    }

    try {
      let gameSystemDict = {};
      _gameSystems.forEach((gameSystem) => {
        gameSystemDict[gameSystem.id] = gameSystem;
      });

      let queryStringParameters = {
        toEvents: true,
        limit: searchLimit,
        sortAscending: false,
      };
      if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
        queryStringParameters.gameSystemId =
          getEnvironmentVariable("SINGLE_GAME_SYSTEM");
      } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
        queryStringParameters.gameSystemManufacturer = getEnvironmentVariable(
          "SINGLE_GAME_MANUFACTURER"
        );
      }

      var data = await handlePaging(
        "/events",
        queryStringParameters,
        eventNextKey,
        setEventNextKey
      );

      data = mapData(data, gameSystemDict, moment);

      setEvents(data);

      setEvents(events?.length ? [...events, ...data] : data);
    } catch (err) {
      postNotification("error", err);
    } finally {
      setEventLoading(false);
    }
  };

  const deleteEvent = (eventId) => {
    var eventArray = [];
    if (events?.length) {
      events.forEach((event) => {
        if (event.id !== eventId) {
          eventArray.push(event);
        }
      });
    }
    setEvents(eventArray);
  };

  const getOrganizations = async () => {
    try {
      const apiGameStores = await handlePagingAll(`/gamestores`, {
        myStores: true,
        sortAscending: false,
        "expand[]": ["location", "owner", "gameStore", "user"],
      });

      apiGameStores.forEach((apiGameStore) => {
        if (apiGameStore.location) {
          apiGameStore.location.displayLocation = "";
          if (apiGameStore.location.city && apiGameStore.location.city !== "") {
            if (apiGameStore.location.displayLocation !== "") {
              apiGameStore.location.displayLocation += ", ";
            }
            apiGameStore.location.displayLocation += apiGameStore.location.city;
          }
          if (
            apiGameStore.location.state &&
            apiGameStore.location.state !== ""
          ) {
            if (apiGameStore.location.displayLocation !== "") {
              apiGameStore.location.displayLocation += ", ";
            }
            apiGameStore.location.displayLocation +=
              apiGameStore.location.state;
          }
          if (
            apiGameStore.location.country &&
            apiGameStore.location.country !== ""
          ) {
            if (apiGameStore.location.displayLocation !== "") {
              apiGameStore.location.displayLocation += ", ";
            }
            apiGameStore.location.displayLocation +=
              apiGameStore.location.country;
          }
        }
      });
      setOrganizations(apiGameStores || []);
    } catch (err) {
      postNotification("error", err);
    } finally {
      setOrganizationsLoading(false);
    }
  };

  const getCircuitTokens = async () => {
    try {
      const tokenData = await fetchCircuitTokenData(authContext);

      setCircuitTokens(tokenData);

      if (tokenData && tokenData.length > 0) {
        const allEventIds = tokenData.map(token => token.eventId).filter(id => id != null);

        const eventIds = [...new Set(allEventIds)];

        if (eventIds.length > 0) {
          try {
            const eventIdParams = eventIds.map(id => `eventIds[]=${encodeURIComponent(id)}`).join('&');
            const limitParam = `limit=${eventIds.length}`;
            const fullRoute = `/events?${eventIdParams}&${limitParam}`;

            const eventsData = await getData(fullRoute, {});
            setTokenEventsData(eventsData?.data);
            
          } catch (eventErr) {
            postNotification("error", `Failed to fetch events for circuit tokens: ${eventErr.message || eventErr}`);
          }
        }
      }
    } catch (err) {
      postNotification("error", err);
    } finally {
      setCircuitTokensLoading(false);
    }
  };


  const parallel = async () => {
    const taskEvents = getEvents();
    const taskOrganizations = getOrganizations();
    const taskCircuitTokens = getCircuitTokens();

    return {
      events: await taskEvents,
      organizations: await taskOrganizations,
      circuitTokens: await taskCircuitTokens,
    };
  };

  useEffect(() => {
    const check = async () => {
      try {
        await parallel();
      } catch (err) {
        postNotification("error", err);
      }
    };

    check();
    // eslint-disable-next-line
  }, []);

  return (
    <Box width="100%">
      <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
        <Grid container sx={{ p: 2, gap: 2 }}>
          <Grid
            item
            xs={12}
            sx={{
              display: { xs: "block", md: "flex" },
              justifyContent: "space-between",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="h2"
                  component="h3"
                  fontWeight="700"
                  gutterBottom
                  sx={{ mb: 4 }}
                >
                  {`Welcome back${
                    authContext?.authState?.identity?.nickname ||
                    authContext?.authState?.identity?.firstName
                      ? ", " +
                        (authContext?.authState?.identity?.nickname ||
                          authContext?.authState?.identity?.firstName)
                      : ""
                  }!`}
                </Typography>
              </Grid>
              {/* Manages Events */}

              <Grid item xs={12}>
                <OrgDashEvents
                  loading={eventLoading}
                  events={events}
                  authContext={authContext}
                  deleteEvent={deleteEvent}
                />
              </Grid>

              <Grid item xs={12}>
                <OrgDashOrganizations
                  loading={organizationsLoading}
                  organizations={organizations}
                />
              </Grid>

              <Grid item xs={12}>
                <OrgDashCircuitTokens 
                  tokens={circuitTokens}
                  loading={circuitTokensLoading}
                  eventsData={tokenEventsData}
                />
              </Grid>

              <Grid item xs={12}>
                <OrgDashTicketSales />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
